// src/lib/api/financial/invoices.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired } from '../shared/validation'
import { computeClientTotal, recoverPayoutBase, STRIPE_FEE_RATE, STRIPE_FEE_FIXED } from '@prosperia/shared'

// Snapshot resync helper. Single rule: payoutBase is authoritative; total,
// surcharge, and client_payment are always derived forward via
// computeClientTotal. Never peel a base out of a grand total via an assumed
// rate — the bundled legacy population spanned both tiers (free 1/(1−0.044)
// and paid 1/(1−0.039)), so any rate-dependent inverse mis-recovers on the
// other tier.
//
// Recovery priority (rate-agnostic):
//   (a) caller-supplied services_subtotal + tax_amount + discount → new base
//   (b) existing row: total_amount − processing_fees_amount (pure subtraction)
// When both are derivable from the existing row, cross-check; throw on
// inconsistency > $0.01 so the caller surfaces it rather than silently
// picking one.
//
// passThrough basis is the ROW's existing state (oldSurcharge > 0.02), not
// the live company toggle — preserves the basis baked at create time.
//
// Triggered only by BASE-affecting fields (services, items, discount, tax).
// Output fields (total_amount / client_payment_amount / processing_fees_amount /
// final_price) are NEVER triggers — they are recomputed and overwritten.
//
// Throws on partial-paid invoices (alreadyPaid > 0): the webhook validates
// each charge against total_amount, so re-snapshotting after a payment lands
// would silently corrupt the invariant.
const round2 = (n) => Math.round(n * 100) / 100
const resyncInvoiceSnapshot = async ({ invoiceId, companyId, baseChange = {} }) => {
  const { data: existing, error: readErr } = await supabase
    .from('invoices')
    .select('total_amount, processing_fees_amount, services_subtotal, tax_amount, status')
    .eq('id', invoiceId)
    .eq('company_id', companyId)
    .single()
  if (readErr || !existing) throw readErr || new Error('Invoice not found for snapshot resync')

  const { data: paidRows, error: payErr } = await supabase
    .from('payments')
    .select('amount')
    .eq('invoice_id', invoiceId)
    .eq('company_id', companyId)
    .eq('status', 'completed')
  if (payErr) throw payErr
  const alreadyPaid = (paidRows || []).reduce((s, p) => s + Number(p.amount || 0), 0)
  if (alreadyPaid > 0) {
    throw new Error(
      `Refusing to resync invoice ${invoiceId}: $${alreadyPaid.toFixed(2)} already paid. ` +
      `The webhook validates each charge against total_amount; changing it now would corrupt the invariant. ` +
      `Product decision needed: re-snapshot (refund delta) vs. lock the row.`
    )
  }

  const recovered = recoverPayoutBase(existing)
  if (recovered.inconsistent) {
    throw new Error(
      `Invoice ${invoiceId} is already inconsistent: ` +
      `services+tax=$${recovered.baseFromParts.toFixed(2)} vs total−surcharge=$${recovered.baseFromTotal.toFixed(2)}. ` +
      `Resync refused — the row needs a manual decision before any edit.`
    )
  }
  const { payoutBase: existingBase, passThrough } = recovered
  const oldServices = Number(existing.services_subtotal) || 0
  const oldTaxAmt = Number(existing.tax_amount) || 0

  // Compute the NEW base from the caller's edit (rate-agnostic). If no
  // base-affecting field changed, keep the existing base.
  const newServices = baseChange.services_subtotal !== undefined
    ? Number(baseChange.services_subtotal) || 0
    : oldServices
  const newTaxAmt = baseChange.tax_amount !== undefined
    ? Number(baseChange.tax_amount) || 0
    : oldTaxAmt
  const baseChanged =
    baseChange.services_subtotal !== undefined ||
    baseChange.tax_amount !== undefined
  const newPayoutBase = baseChanged ? round2(newServices + newTaxAmt) : existingBase

  const { clientTotal, clientSurcharge } = computeClientTotal({
    payoutBase: newPayoutBase,
    passThrough,
  })
  return {
    total_amount: clientTotal,
    client_payment_amount: clientTotal,
    processing_fees_amount: clientSurcharge,
    services_subtotal: newServices,
    tax_amount: newTaxAmt,
  }
}

/**
 * Get all invoices for a company
 * Migrated from supabaseApi.js lines 2143-2160
 */
const _getInvoices = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    .limit(500)

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new invoice
 * Migrated from supabaseApi.js lines 2162-2185
 */
const _createInvoice = async (invoiceData) => {
  const companyId = invoiceData.company_id || invoiceData.companyId || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Generate invoice number if not provided
  let invoiceNumber = invoiceData.invoice_number ?? invoiceData.invoiceNumber
  if (!invoiceNumber) {
    // Generate sequential invoice number
    const { data: existingInvoices } = await supabase
      .from('invoices')
      .select('invoice_number')
      .eq('company_id', companyId)
      .order('invoice_number', { ascending: false })
      .limit(1)
    
    const currentYear = new Date().getFullYear()
    let maxNumber = 0
    
    if (existingInvoices && existingInvoices.length > 0) {
      const yearInvoices = existingInvoices.filter(inv => {
        if (!inv.invoice_number) return false
        const parts = inv.invoice_number.split('-')
        return parts.length >= 3 && parseInt(parts[1]) === currentYear
      })
      
      maxNumber = yearInvoices.reduce((max, inv) => {
        const parts = inv.invoice_number.split('-')
        if (parts.length >= 3) {
          const number = parseInt(parts[2]) || 0
          return Math.max(max, number)
        }
        return max
      }, 0)
    }
    
    const nextNumber = maxNumber + 1
    const paddedNumber = nextNumber.toString().padStart(5, '0')
    invoiceNumber = `INV-${currentYear}-${paddedNumber}`
  }
  
  // Simple approach: prioritize snake_case (database format), fall back to camelCase conversion
  // This handles both formats gracefully
  const convertedData = {
    ...invoiceData,
    // Basic fields - use snake_case if provided, otherwise convert from camelCase
    company_id: companyId,
    client_id: invoiceData.client_id ?? invoiceData.clientId ?? null,
    job_id: invoiceData.job_id ?? invoiceData.jobId ?? null,
    estimate_id: invoiceData.estimate_id ?? invoiceData.estimateId ?? null,
    total_amount: invoiceData.total_amount ?? invoiceData.total ?? 0,
    due_date: invoiceData.due_date ?? invoiceData.dueDate ?? null,
    invoice_number: invoiceNumber,
    // Financial fields - use snake_case if provided, otherwise convert from camelCase
    // Note: discount_amount and discount_type are NOT stored in invoices table
    subtotal: invoiceData.subtotal ?? invoiceData.subtotal,
    tax_amount: invoiceData.tax_amount ?? invoiceData.taxAmount,
    tax_rate: invoiceData.tax_rate ?? invoiceData.taxRate,
    deposit_amount: invoiceData.deposit_amount ?? invoiceData.depositAmount,
    services_subtotal: invoiceData.services_subtotal ?? invoiceData.servicesSubtotal,
    client_payment_amount: invoiceData.client_payment_amount ?? invoiceData.clientPaymentAmount,
    processing_fees_amount: invoiceData.processing_fees_amount ?? invoiceData.processingFeesAmount,
    // Recurring fields - use snake_case if provided, otherwise convert from camelCase
    is_recurring: invoiceData.is_recurring ?? invoiceData.isRecurring,
    recurring_frequency: invoiceData.recurring_frequency ?? invoiceData.recurringFrequency,
    recurring_interval_count: invoiceData.recurring_interval_count ?? invoiceData.recurringIntervalCount,
    recurring_start_date: invoiceData.recurring_start_date ?? invoiceData.recurringStartDate,
    recurring_end_date: invoiceData.recurring_end_date ?? invoiceData.recurringEndDate,
    recurring_next_run_date: invoiceData.recurring_next_run_date ?? invoiceData.recurringNextRunDate,
    recurring_parent_id: invoiceData.recurring_parent_id ?? invoiceData.recurringParentId,
    recurring_sequence: invoiceData.recurring_sequence ?? invoiceData.recurringSequence,
    recurring_require_approval: invoiceData.recurring_require_approval ?? invoiceData.recurringRequireApproval,
    recurring_assigned_employee_ids: invoiceData.recurring_assigned_employee_ids ?? invoiceData.recurringAssignedEmployeeIds ?? [],
    recurring_assigned_team_id: invoiceData.recurring_assigned_team_id ?? invoiceData.recurringAssignedTeamId ?? null,
    recurring_job_time: invoiceData.recurring_job_time ?? invoiceData.recurringJobTime ?? null,
    recurring_job_duration_minutes: invoiceData.recurring_job_duration_minutes ?? invoiceData.recurringJobDurationMinutes ?? null,
    // Rebill tracking
    rebilled_at: invoiceData.rebilled_at ?? invoiceData.rebilledAt,
    // System fields
    created_by: (await getValidatedUserId()) || null
  }
  
  // Remove any camelCase fields that might conflict (clean up)
  delete convertedData.companyId
  delete convertedData.clientId
  delete convertedData.jobId
  delete convertedData.estimateId
  delete convertedData.total
  delete convertedData.dueDate
  delete convertedData.invoiceNumber
  delete convertedData.taxAmount
  delete convertedData.taxRate
  delete convertedData.discountAmount
  delete convertedData.discountType
  delete convertedData.depositAmount
  delete convertedData.discount_amount
  delete convertedData.discount_type
  delete convertedData.servicesSubtotal
  delete convertedData.clientPaymentAmount
  delete convertedData.processingFeesAmount
  delete convertedData.isRecurring
  delete convertedData.recurringFrequency
  delete convertedData.recurringIntervalCount
  delete convertedData.recurringStartDate
  delete convertedData.recurringEndDate
  delete convertedData.recurringNextRunDate
  delete convertedData.recurringParentId
  delete convertedData.recurringSequence
  delete convertedData.recurringRequireApproval
  delete convertedData.recurringAssignedEmployeeIds
  delete convertedData.recurringAssignedTeamId
  delete convertedData.recurringJobTime
  delete convertedData.recurringJobDurationMinutes
  delete convertedData.rebilledAt
  
  // INSERT + re-SELECT with the joined client. PostgREST's `.insert().select(embed)`
  // chain is unreliable against this project's RLS setup (same pattern as
  // _createEstimate / _updateEstimate), so split the embed into a separate
  // SELECT. Returning the server row in `_getInvoices` shape lets per-entity
  // cache reconcile do a whole-row replace by id without consumers ever
  // seeing a partial/joinless row.
  const { data: inserted, error: insertError } = await supabase
    .from('invoices')
    .insert([convertedData])
    .select('id')
    .single()

  if (insertError) throw insertError

  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('id', inserted.id)
    .eq('company_id', companyId)
    .single()

  if (error) throw error
  return data
}

/**
 * Update an existing invoice
 * Migrated from supabaseApi.js lines 2187-2220
 */
const _updateInvoice = async (invoiceId, invoiceData) => {
  validateRequired(invoiceId, 'Invoice ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Convert camelCase to snake_case for Supabase schema
  const convertedData = {}
  
  if (invoiceData.clientId !== undefined) convertedData.client_id = invoiceData.clientId
  if (invoiceData.jobId !== undefined) convertedData.job_id = invoiceData.jobId
  if (invoiceData.estimateId !== undefined) convertedData.estimate_id = invoiceData.estimateId
  // Outputs and base-affecting fields are tracked separately so we can
  // throw loud on the "bare total-only" pattern (silent no-op = user
  // confusion). The strip is defensive — convertedData starts {} and only
  // populates from a known camelCase list, but if anyone broadens that list
  // later this guards them too.
  const outputKeys = ['total', 'totalAmount', 'total_amount',
                      'client_payment_amount', 'clientPaymentAmount',
                      'processing_fees_amount', 'processingFeesAmount']
  for (const k of outputKeys) delete convertedData[k]
  const hasOutput = outputKeys.some(k => invoiceData[k] !== undefined)

  const baseChange = {}
  if (invoiceData.services_subtotal !== undefined) baseChange.services_subtotal = invoiceData.services_subtotal
  if (invoiceData.servicesSubtotal !== undefined) baseChange.services_subtotal = invoiceData.servicesSubtotal
  if (invoiceData.tax_amount !== undefined) baseChange.tax_amount = invoiceData.tax_amount
  if (invoiceData.taxAmount !== undefined) baseChange.tax_amount = invoiceData.taxAmount

  if (hasOutput && Object.keys(baseChange).length === 0 && !invoiceData.__grandTotalEdit) {
    // Caller is trying to change a derived field directly. That would be a
    // silent no-op under the old code (output stripped, no resync trigger).
    // Force them to either (a) send base fields, or (b) opt-in to the
    // explicit grand-total entrypoint below.
    throw new Error(
      'total / client_payment_amount / processing_fees_amount are derived. ' +
      'Send services_subtotal/tax_amount, or pass __grandTotalEdit:true to ' +
      'invert a new grand client total against the row\'s passThrough basis.'
    )
  }

  if (invoiceData.__grandTotalEdit === true) {
    // Explicit grand-total entrypoint. Caller has typed a new GRAND CLIENT
    // total in the UI. Invert it against the row's passThrough basis to
    // recover the new payoutBase, then derive forward via the canonical
    // model so all three columns stay self-consistent.
    const newGrandTotal = Number(
      invoiceData.total ?? invoiceData.totalAmount ?? invoiceData.total_amount
    ) || 0
    if (newGrandTotal <= 0) {
      throw new Error('__grandTotalEdit requires a positive total / totalAmount / total_amount')
    }
    const { data: existing, error: readErr } = await supabase
      .from('invoices')
      .select('processing_fees_amount')
      .eq('id', invoiceId)
      .eq('company_id', companyId)
      .single()
    if (readErr || !existing) throw readErr || new Error('Invoice not found for grand-total edit')
    const passThrough = Number(existing.processing_fees_amount) > 0.02
    // INTENTIONAL Stripe-only inverse on shared STRIPE_FEE_RATE/
    // STRIPE_FEE_FIXED. Distinct from the deleted BUNDLED-recovery inverse —
    // that one assumed the wrong denominator on legacy rows. This one is the
    // forward Stripe-only model's algebraic inverse, used only when the
    // caller is providing a brand-new grand total (the row's own
    // passThrough basis decides whether to invert at all). recoverPayoutBase
    // is still rate-agnostic; this is the only fee-rate inverse in
    // production code.
    const newPayoutBase = passThrough
      ? Math.max(0, newGrandTotal * (1 - STRIPE_FEE_RATE) - STRIPE_FEE_FIXED)
      : newGrandTotal
    baseChange.services_subtotal = Math.round(newPayoutBase * 100) / 100
    baseChange.tax_amount = 0 // grand-total path collapses services+tax into one base
  }

  if (Object.keys(baseChange).length > 0) {
    const resynced = await resyncInvoiceSnapshot({ invoiceId, companyId, baseChange })
    Object.assign(convertedData, resynced)
  }
  if (invoiceData.status !== undefined) convertedData.status = invoiceData.status
  if (invoiceData.dueDate !== undefined) convertedData.due_date = invoiceData.dueDate
  if (invoiceData.notes !== undefined) convertedData.notes = invoiceData.notes
  if (invoiceData.invoiceNumber !== undefined) convertedData.invoice_number = invoiceData.invoiceNumber
  
  // Recurring fields
  if (invoiceData.isRecurring !== undefined) convertedData.is_recurring = invoiceData.isRecurring
  if (invoiceData.recurringFrequency !== undefined) convertedData.recurring_frequency = invoiceData.recurringFrequency
  if (invoiceData.recurringIntervalCount !== undefined) convertedData.recurring_interval_count = invoiceData.recurringIntervalCount
  if (invoiceData.recurringStartDate !== undefined) convertedData.recurring_start_date = invoiceData.recurringStartDate
  if (invoiceData.recurringEndDate !== undefined) convertedData.recurring_end_date = invoiceData.recurringEndDate
  if (invoiceData.recurringNextRunDate !== undefined) convertedData.recurring_next_run_date = invoiceData.recurringNextRunDate
  if (invoiceData.recurringParentId !== undefined) convertedData.recurring_parent_id = invoiceData.recurringParentId
  if (invoiceData.recurringSequence !== undefined) convertedData.recurring_sequence = invoiceData.recurringSequence
  if (invoiceData.recurringRequireApproval !== undefined) convertedData.recurring_require_approval = invoiceData.recurringRequireApproval
  if (invoiceData.recurringAssignedEmployeeIds !== undefined) convertedData.recurring_assigned_employee_ids = invoiceData.recurringAssignedEmployeeIds
  if (invoiceData.recurringAssignedTeamId !== undefined) convertedData.recurring_assigned_team_id = invoiceData.recurringAssignedTeamId
  if (invoiceData.recurringJobTime !== undefined) convertedData.recurring_job_time = invoiceData.recurringJobTime
  if (invoiceData.recurringJobDurationMinutes !== undefined) convertedData.recurring_job_duration_minutes = invoiceData.recurringJobDurationMinutes

  // Also handle snake_case directly
  if (invoiceData.is_recurring !== undefined) convertedData.is_recurring = invoiceData.is_recurring
  if (invoiceData.recurring_frequency !== undefined) convertedData.recurring_frequency = invoiceData.recurring_frequency
  if (invoiceData.recurring_interval_count !== undefined) convertedData.recurring_interval_count = invoiceData.recurring_interval_count
  if (invoiceData.recurring_start_date !== undefined) convertedData.recurring_start_date = invoiceData.recurring_start_date
  if (invoiceData.recurring_end_date !== undefined) convertedData.recurring_end_date = invoiceData.recurring_end_date
  if (invoiceData.recurring_next_run_date !== undefined) convertedData.recurring_next_run_date = invoiceData.recurring_next_run_date
  if (invoiceData.recurring_parent_id !== undefined) convertedData.recurring_parent_id = invoiceData.recurring_parent_id
  if (invoiceData.recurring_sequence !== undefined) convertedData.recurring_sequence = invoiceData.recurring_sequence
  if (invoiceData.recurring_require_approval !== undefined) convertedData.recurring_require_approval = invoiceData.recurring_require_approval
  if (invoiceData.recurring_assigned_employee_ids !== undefined) convertedData.recurring_assigned_employee_ids = invoiceData.recurring_assigned_employee_ids
  if (invoiceData.recurring_assigned_team_id !== undefined) convertedData.recurring_assigned_team_id = invoiceData.recurring_assigned_team_id
  if (invoiceData.recurring_job_time !== undefined) convertedData.recurring_job_time = invoiceData.recurring_job_time
  if (invoiceData.recurring_job_duration_minutes !== undefined) convertedData.recurring_job_duration_minutes = invoiceData.recurring_job_duration_minutes

  // UPDATE then re-SELECT with the joined client (same reason as
  // _createInvoice / _updateEstimate: PostgREST's update+select(embed)
  // chain hangs against this project's RLS setup). Returning the full
  // server row matches the `_getInvoices` shape so per-entity cache
  // reconcile can do a whole-row replace by id; callers that previously
  // depended on the hand-shaped camelCase wrapper here (only
  // supabaseData.updateInvoice) already re-shape downstream.
  const { error: updateError } = await supabase
    .from('invoices')
    .update(convertedData)
    .eq('id', invoiceId)
    .eq('company_id', companyId)

  if (updateError) throw updateError

  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('id', invoiceId)
    .eq('company_id', companyId)
    .single()

  if (error) throw error
  return data
}

/**
 * Delete an invoice
 * Migrated from supabaseApi.js lines 2222-2238
 */
const _deleteInvoice = async (invoiceId) => {
  validateRequired(invoiceId, 'Invoice ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('invoices')
    .delete()
    .eq('id', invoiceId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get invoice by ID
 */
const _getInvoiceById = async (invoiceId) => {
  validateRequired(invoiceId, 'Invoice ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent),
      job:jobs(title),
      estimate:estimates(estimate_number, final_price)
    `)
    .eq('id', invoiceId)
    .eq('company_id', companyId)
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update invoice status
 */
const _updateInvoiceStatus = async (invoiceId, status) => {
  validateRequired(invoiceId, 'Invoice ID')
  validateRequired(status, 'Status')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('invoices')
    .update({ 
      status, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', invoiceId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Get invoices by status
 */
const _getInvoicesByStatus = async (status, companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('company_id', resolvedCompanyId)
    .eq('status', status)
    .order('created_at', { ascending: false })
    .limit(500)

  if (error) throw error
  return createSuccessResponse(data || [])
}

// Export wrapped functions
export const getInvoices = withErrorHandling(_getInvoices, 'fetch invoices', 'invoices')
export const createInvoice = withErrorHandling(_createInvoice, 'create invoice', 'invoice')
export const updateInvoice = withErrorHandling(_updateInvoice, 'update invoice', 'invoice')
export const deleteInvoice = withErrorHandling(_deleteInvoice, 'delete invoice', 'invoice')
export const getInvoiceById = withErrorHandling(_getInvoiceById, 'get invoice by ID', 'invoice')
export const updateInvoiceStatus = withErrorHandling(_updateInvoiceStatus, 'update invoice status', 'invoice')
export const getInvoicesByStatus = withErrorHandling(_getInvoicesByStatus, 'get invoices by status', 'invoices')